import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { setAccessTokenProvider } from '@/lib/api';
import { getSupabaseClient, readProfile, readTenantId } from '@/lib/supabase';

import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Events that mean "there is no usable session any more". `TOKEN_REFRESHED` is not among them:
 * Supabase does not report a *failed* refresh as its own event — it drops the session and emits
 * `SIGNED_OUT`, which is why a refresh that cannot be completed lands here and turns into the
 * `unauthenticated` status that `RequireAuth` bounces to `/login`.
 */
const TERMINAL_EVENTS: readonly AuthChangeEvent[] = ['SIGNED_OUT'];

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const sessionRef = useRef<Session | null>(null);

  sessionRef.current = session;

  useEffect(() => {
    const supabase = getSupabaseClient();
    let active = true;

    // The API client lives outside React; give it a live view of the token instead of a copy
    // that would go stale on every silent refresh.
    setAccessTokenProvider(() => sessionRef.current?.access_token ?? null);

    // `getSession` reads the persisted session from storage and refreshes it when it is already
    // past its expiry, which is what makes a reload keep the user signed in.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) {
        return;
      }
      setSession(data.session);
      setStatus(data.session === null ? 'unauthenticated' : 'authenticated');
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      const resolved = TERMINAL_EVENTS.includes(event) ? null : nextSession;
      setSession(resolved);
      setStatus(resolved === null ? 'unauthenticated' : 'authenticated');
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
      setAccessTokenProvider(() => null);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();

    // The local session is dropped either way: a network failure while revoking the refresh
    // token must not leave the browser holding a session the user believes they ended.
    setSession(null);
    setStatus('unauthenticated');

    if (error) {
      throw error;
    }
  }, []);

  const requestPasswordReset = useCallback(async (email: string, redirectTo: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) {
      throw error;
    }
  }, []);

  const verifyRecovery = useCallback(async (tokenHash: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
    if (error) {
      throw error;
    }
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      throw error;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      tenantId: readTenantId(session),
      profile: readProfile(session),
      signIn,
      signOut,
      requestPasswordReset,
      verifyRecovery,
      updatePassword,
    }),
    [status, session, signIn, signOut, requestPasswordReset, verifyRecovery, updatePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
